import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../../../store/slices/authentication";
import { SharedHorizontalTabs } from "../../shared";
import BlockedFeatureBanner from "../../shared/BlockedFeatureBanner";
import BulletinPosts from "./Posts";
import CreatePost from "./CreatePost";
import Footprint from "../Footprint";
import { blogAddressFor } from "./blogLink";

// "Landing" was removed: its welcome copy now lives as a collapsible intro on the
// Posts tab, so opening the Bulletin shows live posts rather than static text.
// Old ?tab=landing links fall through to index 0 (Posts) via useGetTabIndex.
const TABS = [
  { key: "posts", label: "Posts" },
  { key: "createPost", label: "Create Post" },
];

// The page's top row (Sam, 2026-09-26): the Bulletin Board first, then the Blog
// (code name "Footprint"), which now lives here. The board's own tabs already
// use "tab" in the address, so this row uses "view".
const VIEWS = [
  { key: "board", label: "Bulletin Board" },
  { key: "blog", label: "Blog" },
];

function Bulletin() {
  const user = useSelector(selectUser);
  const individualId = user?.externalId ? parseInt(user.externalId) : null;
  const { currentTabIndex, setSearchParams } =
    SharedHorizontalTabs.useGetTabIndex(TABS);
  const { currentTabIndex: currentViewIndex } =
    SharedHorizontalTabs.useGetTabIndex(VIEWS, "view");
  const isBlog = VIEWS[currentViewIndex]?.key === "blog";
  const { search, hash } = useLocation();

  // Switching views starts the new one at its own first tab.
  const handleViewChange = (index) => {
    setSearchParams({ view: VIEWS[index].key }, { replace: true });
  };

  // Tab change handler to be passed to the HorizontalTabs component
  const handleTabChange = (index) => {
    setSearchParams({ tab: TABS[index].key }, { replace: true });
  };

  // Switch on the tab KEY, not its position, so reordering or removing a tab
  // can never silently render the wrong panel.
  const renderTabContent = () => {
    switch (TABS[currentTabIndex]?.key) {
      case "createPost":
        return <CreatePost />;
      case "posts":
      default:
        return <BulletinPosts />;
    }
  };

  // An old-style Blog link (?tab=history) goes to the Blog, not the board.
  const blogAddress = blogAddressFor(search, hash);
  if (blogAddress) return <Navigate to={blogAddress} replace />;

  return (
    <div className="min-h-screen flex flex-col px-4 md:px-[60px] py-[30px] gap-y-[40px] bg-dupontCream dark:bg-darkPrimary transition-colors duration-200">
      <div className="flex flex-col items-center">
        <div className="flex flex-col items-center text-center">
          <h1 className="text-[42px] font-display font-bold text-dupontBlue dark:text-darkText mb-[12px]">
            Bulletin &amp; Blog
          </h1>
          <div className="w-[80px] h-[1px] bg-dupontGold"></div>
        </div>
      </div>
      <div className="flex flex-col gap-y-[30px]">
        <SharedHorizontalTabs.Component
          tabs={VIEWS.map((v) => v.label)}
          initialIndex={currentViewIndex}
          onChange={handleViewChange}
        />
        {/* Each view keeps its own block check: a member blocked from the
            board can still read the Blog, and the other way round. */}
        {isBlog ? (
          <Footprint />
        ) : (
          <BlockedFeatureBanner
            featureKey="is_bulletin_blocked"
            featureName="Bulletin Board"
            individualId={individualId}
            mode="full"
          >
            <div className="flex flex-col gap-y-[30px]">
              <SharedHorizontalTabs.Component
                tabs={TABS.map((tab) => tab.label)}
                initialIndex={currentTabIndex}
                onChange={handleTabChange}
              />
              {renderTabContent()}
            </div>
          </BlockedFeatureBanner>
        )}
      </div>
    </div>
  );
}

export default Bulletin;
